import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface ChatBackgroundRendererProps {
  backgroundType: 'solid' | 'image' | 'video';
  backgroundImagePath: string | null;
  backgroundMediaType: 'image' | 'video' | null;
  backgroundObjectPosition: string | null;
  backgroundNeedsCrossfadeLoop: boolean;
  opacity?: number;
  isNewConversation?: boolean;
  showVideoIndicator?: boolean; // 是否显示视频标识
  className?: string;
}

export const ChatBackgroundRenderer: React.FC<ChatBackgroundRendererProps> = ({
  backgroundType,
  backgroundImagePath,
  backgroundMediaType,
  backgroundObjectPosition,
  backgroundNeedsCrossfadeLoop,
  opacity,
  isNewConversation = false,
  showVideoIndicator = false,
  className = ''
}) => {
  const { t } = useTranslation(['common']);
  // 视频交叉叠化循环状态
  const video1Ref = useRef<HTMLVideoElement>(null);
  const video2Ref = useRef<HTMLVideoElement>(null);
  const [activeVideo, setActiveVideo] = useState<1 | 2>(1);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // 交叉叠化循环处理函数
  const handleVideoCrossfade = useCallback(() => {
    if (!backgroundNeedsCrossfadeLoop) return;

    const currentVideo = activeVideo === 1 ? video1Ref.current : video2Ref.current;
    const nextVideo = activeVideo === 1 ? video2Ref.current : video1Ref.current;

    if (!currentVideo || !nextVideo) return;

    // 开始转场
    setIsTransitioning(true);

    // 重置下一个视频到开头并开始播放
    nextVideo.currentTime = 0;
    nextVideo.play();

    // 2秒后完成转场
    setTimeout(() => {
      setActiveVideo(activeVideo === 1 ? 2 : 1);
      setIsTransitioning(false);
      // 暂停当前视频
      currentVideo.pause();
    }, 2000);
  }, [backgroundNeedsCrossfadeLoop, activeVideo]);

  // 监听视频时间更新，在接近结尾时开始交叉叠化
  const handleVideoTimeUpdate = useCallback(() => {
    if (!backgroundNeedsCrossfadeLoop || isTransitioning) return;

    const currentVideo = activeVideo === 1 ? video1Ref.current : video2Ref.current;
    if (!currentVideo) return;

    // 在视频结束前2秒开始交叉叠化
    const fadeStartTime = currentVideo.duration - 2;
    if (currentVideo.currentTime >= fadeStartTime) {
      handleVideoCrossfade();
    }
  }, [backgroundNeedsCrossfadeLoop, activeVideo, isTransitioning, handleVideoCrossfade]);

  // 计算透明度
  const getOpacity = () => {
    if (opacity !== undefined) return opacity;
    return isNewConversation ? 0.4 : 0.8;
  };

  if (backgroundType === 'solid' || !backgroundImagePath || !backgroundMediaType) {
    return null;
  }

  return (
    <div className={`absolute inset-0 z-0 ${className}`}>
      {backgroundMediaType === 'video' ? (
        backgroundNeedsCrossfadeLoop ? (
          // 交叉叠化模式：两个视频元素
          <div className="relative w-full h-full">
            <video
              ref={video1Ref}
              className="w-full h-full object-cover absolute inset-0 transition-all duration-[2000ms]"
              autoPlay
              muted
              playsInline
              preload="auto"
              onTimeUpdate={activeVideo === 1 ? handleVideoTimeUpdate : undefined}
              style={{
                opacity: activeVideo === 1
                  ? (isTransitioning ? 0 : getOpacity())
                  : (isTransitioning ? getOpacity() : 0),
                objectPosition: backgroundObjectPosition || 'center center',
                filter: isTransitioning && activeVideo === 1 ? 'blur(2px)' : 'none'
              }}
            >
              <source src={backgroundImagePath} type="video/mp4" />
            </video>
            <video
              ref={video2Ref}
              className="w-full h-full object-cover absolute inset-0 transition-all duration-[2000ms]"
              muted
              playsInline
              preload="auto"
              onTimeUpdate={activeVideo === 2 ? handleVideoTimeUpdate : undefined}
              style={{
                opacity: activeVideo === 2
                  ? (isTransitioning ? 0 : getOpacity())
                  : (isTransitioning ? getOpacity() : 0),
                objectPosition: backgroundObjectPosition || 'center center',
                filter: isTransitioning && activeVideo === 2 ? 'blur(2px)' : 'none'
              }}
            >
              <source src={backgroundImagePath} type="video/mp4" />
            </video>
            {/* 视频标识 - 只在预览模式显示 */}
            {showVideoIndicator && (
              <div className="absolute top-2 left-2 bg-black bg-opacity-50 rounded px-2 py-1 text-xs text-white z-10">
                {t('common:media.video', '📹 视频')}
              </div>
            )}
          </div>
        ) : (
          // 普通模式：单个视频元素
          <div className="relative w-full h-full">
            <video
              className="w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              style={{
                opacity: getOpacity(),
                objectPosition: backgroundObjectPosition || 'center center'
              }}
            >
              <source src={backgroundImagePath} type="video/mp4" />
            </video>
            {/* 视频标识 - 只在预览模式显示 */}
            {showVideoIndicator && (
              <div className="absolute top-2 left-2 bg-black bg-opacity-50 rounded px-2 py-1 text-xs text-white z-10">
                {t('common:media.video', '📹 视频')}
              </div>
            )}
          </div>
        )
      ) : (
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `url(${backgroundImagePath})`,
            backgroundSize: 'cover',
            backgroundPosition: backgroundObjectPosition || 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: getOpacity()
          }}
        />
      )}
    </div>
  );
};
